import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, UserPlus, Edit2, Trash2, ArrowLeft } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "@/hooks/use-toast";
import { clientSchema } from "@/lib/validation";
import logo from "@/assets/logo.png";

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    telegram_chat_id: "",
    channel_pref: "email",
  });

  useEffect(() => {
    checkUser();
    fetchClients();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/");
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading clients",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Validate form data
      const validation = clientSchema.safeParse(formData);
      if (!validation.success) {
        const firstError = validation.error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (editingId) {
        const { error } = await supabase
          .from("clients")
          .update(formData)
          .eq("id", editingId);

        if (error) throw error;
        toast({ title: "Client updated successfully!" });
      } else {
        const { error } = await supabase
          .from("clients")
          .insert({ ...formData, user_id: user.id });

        if (error) throw error;
        toast({ title: "Client added successfully!" });
      }

      setFormData({ name: "", contact: "", email: "", telegram_chat_id: "", channel_pref: "email" });
      setEditingId(null);
      setShowForm(false);
      fetchClients();
    } catch (error: any) {
      toast({
        title: "Error saving client",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (client: any) => {
    setFormData({
      name: client.name,
      contact: client.contact,
      email: client.email || "",
      telegram_chat_id: client.telegram_chat_id || "",
      channel_pref: client.channel_pref,
    });
    setEditingId(client.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this client?")) return;

    try {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Client deleted successfully!" });
      fetchClients();
    } catch (error: any) {
      toast({
        title: "Error deleting client",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logo} alt="SnapBill" className="h-8 md:h-10 w-8 md:w-10" />
            <div className="hidden sm:block">
              <h1 className="font-display font-bold text-lg md:text-xl">SnapBill</h1>
              <p className="text-xs text-muted-foreground">Snap. Bill. Paid.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="hidden md:flex">
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="md:hidden">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-4 md:py-8 px-4 mb-20 md:mb-8">
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Client Management</h2>
              <p className="text-sm md:text-base text-muted-foreground">Manage your client contacts and preferences</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => navigate("/history")} className="w-full sm:w-auto">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={() => {
                setFormData({ name: "", contact: "", email: "", telegram_chat_id: "", channel_pref: "email" });
                setEditingId(null);
                setShowForm(!showForm);
              }} className="w-full sm:w-auto">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </div>
          </div>

          {showForm && (
            <Card>
              <CardHeader>
                <CardTitle>{editingId ? "Edit Client" : "Add New Client"}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Client Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact">Phone Number</Label>
                    <Input
                      id="contact"
                      type="tel"
                      placeholder="+60123456789"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="client@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telegram_chat_id">
                      Telegram Chat ID (Optional)
                    </Label>
                    <Input
                      id="telegram_chat_id"
                      type="text"
                      placeholder="@username or 123456789"
                      value={formData.telegram_chat_id}
                      onChange={(e) => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Required for sending invoices via Telegram
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="channel">Preferred Contact Method</Label>
                    <Select
                      value={formData.channel_pref}
                      onValueChange={(v) => setFormData({ ...formData, channel_pref: v })}
                    >
                      <SelectTrigger id="channel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="telegram">Telegram</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">
                      {editingId ? "Update Client" : "Add Client"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                        setFormData({ name: "", contact: "", email: "", telegram_chat_id: "", channel_pref: "email" });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading clients...
              </CardContent>
            </Card>
          ) : clients.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <UserPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No clients yet</p>
                <Button onClick={() => setShowForm(true)}>
                  Add your first client
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {clients.map((client) => (
                <Card key={client.id}>
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg">{client.name}</h3>
                        <p className="text-sm text-muted-foreground break-all">📱 {client.contact}</p>
                        {client.email && (
                          <p className="text-sm text-muted-foreground break-all">✉️ {client.email}</p>
                        )}
                        {client.telegram_chat_id && (
                          <p className="text-sm text-muted-foreground break-all">📱 Telegram: {client.telegram_chat_id}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Prefers: {client.channel_pref}
                        </p>
                      </div>
                      <div className="flex gap-2 self-end sm:self-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(client)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(client.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Clients;
